import Loader from 'components/Loader';
import { saveSection } from 'components/section-edit/saveSection';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { SectionSaveRefused } from 'components/section-edit/SectionSaveRefused';
import { ReactNode } from 'react';
import { useGetCorrespondenceByIdQuery, useUpdateCorrespondenceMutation } from 'store/api/correspondence';
import { ClassifierData } from 'types/classifiers';
import { CorrespondenceDetailsDto, CorrespondenceForm } from 'types/correspondence';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import {
    areSendersComplete,
    CORRESPONDENCE_FORM_CLASSIFIERS,
    CORRESPONDENCE_SECTION_FIELDS,
    CorrespondenceSectionId,
    correspondenceDtoToForm,
    correspondenceFormToRequest,
} from '../correspondenceFormMapping';
import {
    ConnectedClientCaseFields,
    DmsFields,
    GeneralSectionFields,
    NoteFields,
    OfficialFields,
    SendersFields,
} from './CorrespondenceFields';
import { CorrespondenceFormState, useCorrespondenceForm } from './useCorrespondenceForm';

const SECTION_FIELDS: Record<CorrespondenceSectionId, (form: CorrespondenceFormState) => ReactNode> = {
    general: (form) => <GeneralSectionFields form={form} />,
    official: (form) => <OfficialFields form={form} />,
    senders: (form) => <SendersFields form={form} />,
    note: (form) => <NoteFields form={form} />,
    connected: (form) => <ConnectedClientCaseFields form={form} />,
    dms: (form) => <DmsFields form={form} />,
};

interface SectionFormProps {
    section: CorrespondenceSectionId;
    correspondence: CorrespondenceDetailsDto;
    initialData: CorrespondenceForm;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
}

function CorrespondenceSectionForm({ section, correspondence, initialData, classifiers, onClose }: SectionFormProps) {
    const form = useCorrespondenceForm({ type: 'edit', initialData, classifiers });
    // The page's argument (the route's id), so the save refreshes the page's own cache entry.
    const { refetch } = useGetCorrespondenceByIdQuery(String(correspondence.id));
    const [updateCorrespondence] = useUpdateCorrespondenceMutation();

    const onSave = (values: CorrespondenceForm) =>
        saveSection(values, {
            section,
            sectionFields: CORRESPONDENCE_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto) => correspondenceDtoToForm(dto, classifiers),
            toRequest: (edited) => {
                // General holds the status and the type that make the senders' fields required, which only the
                // Sender / Recipient card's fields check.
                if (section === 'general' && !areSendersComplete(edited)) {
                    throw new SectionSaveRefused(t('correspondence.complete-senders-first'));
                }
                return { ...correspondenceFormToRequest(edited), id: String(correspondence.id) };
            },
            update: (request) => updateCorrespondence(request).unwrap(),
            onSaved: onClose,
        });

    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: CorrespondenceSectionId;
    correspondence: CorrespondenceDetailsDto;
    onClose: () => void;
}

function CorrespondenceSectionEditor({ section, correspondence, onClose }: EditorProps) {
    const { classifiers, isLoading } = useClassifiers({ codes: CORRESPONDENCE_FORM_CLASSIFIERS });

    const initialData = useFrozenOnceLoaded(!isLoading, () => correspondenceDtoToForm(correspondence, classifiers));

    if (!initialData) {
        return <Loader />;
    }
    return (
        <CorrespondenceSectionForm
            section={section}
            correspondence={correspondence}
            initialData={initialData}
            classifiers={classifiers}
            onClose={onClose}
        />
    );
}

interface CorrespondenceSectionProps {
    id: CorrespondenceSectionId;
    titleId: string;
    correspondence: CorrespondenceDetailsDto;
    /** The read view, handed the section's Edit control. */
    children: (edit: ReactNode) => ReactNode;
}

/**
 * A correspondence section on the record page that edits in place with the correspondence form's fields for that
 * section. While it edits it holds the correspondence's editing session, which the page's provider names.
 */
export function CorrespondenceSection({ id, titleId, correspondence, children }: CorrespondenceSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => (
                <CorrespondenceSectionEditor section={id} correspondence={correspondence} onClose={close} />
            )}
        >
            {children}
        </SectionEdit>
    );
}
